// app/src/simulations/sendContributeV2.ts
import {
  Connection,
  PublicKey,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js";
import { Buffer } from "buffer";
import { buildContributeV2Instruction } from "./buildContributeV2Ix";

/**
 * Sends a real contribute_v2 transaction (Option A):
 * - Builds ix from IDL (on-chain IDL shipped in app/src/idl/weare_susu.json)
 * - Builds v0 VersionedTransaction
 * - Signs with Phantom (or injected wallet override)
 * - Sends and confirms
 *
 * Best-practice policy (your project):
 * - Derive PDAs (member_v2, vault_v2) by default.
 * - If overrides are provided, validate against derived and hard-fail on mismatch.
 */
function deriveMemberV2Pda(programId: PublicKey, poolV2: PublicKey, wallet: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("member_v2"), poolV2.toBuffer(), wallet.toBuffer()],
    programId
  )[0];
}

function deriveVaultV2Pda(programId: PublicKey, poolV2: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("vault_v2"), poolV2.toBuffer()],
    programId
  )[0];
}

function asTrimmed(v: unknown) {
  return String(v ?? "").trim();
}

export async function sendContributeV2(params: {
  programId: string;
  endpoint: string;

  pool_v2: string;

  /**
   * Optional overrides (Advanced mode):
   * If present, must match derived PDAs.
   */
  member_v2?: string;
  vault_v2?: string;

  lamports: string;

  commitment?: "processed" | "confirmed" | "finalized";
  wallet?: any; // optional override; defaults to window.solana

  /**
   * Fee headroom (lamports) to avoid "insufficient funds" due to fees.
   * Traditional control: keep a buffer.
   */
  feeBufferLamports?: number;
}) {
  const commitment = params.commitment ?? "confirmed";
  const connection = new Connection(params.endpoint, commitment);
  const programId = new PublicKey(asTrimmed(params.programId));

  // Wallet: Phantom default
  const w = typeof window !== "undefined" ? (window as any) : {};
  const wallet = params.wallet ?? w.solana;

  if (!wallet) throw new Error("No wallet found. Connect Phantom.");
  if (!wallet.publicKey) throw new Error("Wallet not connected (missing publicKey).");

  // Most wallets expose signTransaction for v0; signAndSendTransaction is optional.
  const canSignTx = typeof wallet.signTransaction === "function";
  const canSignAndSend = typeof wallet.signAndSendTransaction === "function";

  if (!canSignTx && !canSignAndSend) {
    throw new Error("Wallet does not support signTransaction/signAndSendTransaction.");
  }

  const feePayer = new PublicKey(wallet.publicKey.toString());
  const poolPk = new PublicKey(asTrimmed(params.pool_v2));

  // Derive PDAs per IDL seeds (Option A)
  const derivedMember = deriveMemberV2Pda(programId, poolPk, feePayer);
  const derivedVault = deriveVaultV2Pda(programId, poolPk);

  const memberOverride = asTrimmed(params.member_v2);
  const vaultOverride = asTrimmed(params.vault_v2);

  const memberPk = memberOverride ? new PublicKey(memberOverride) : derivedMember;
  const vaultPk = vaultOverride ? new PublicKey(vaultOverride) : derivedVault;

  if (memberOverride && !memberPk.equals(derivedMember)) {
    throw new Error(
      `member_v2 mismatch.\n` +
        `- provided: ${memberPk.toBase58()}\n` +
        `- derived:  ${derivedMember.toBase58()}\n` +
        `Fix the pasted PDA or omit member_v2 so it is derived.`
    );
  }

  if (vaultOverride && !vaultPk.equals(derivedVault)) {
    throw new Error(
      `vault_v2 mismatch.\n` +
        `- provided: ${vaultPk.toBase58()}\n` +
        `- derived:  ${derivedVault.toBase58()}\n` +
        `Fix the pasted PDA or omit vault_v2 so it is derived.`
    );
  }

  // Guardrail: balance check with fee headroom
  const bal = await connection.getBalance(feePayer, commitment);
  const lamports = BigInt(asTrimmed(params.lamports));
  const feeBuf = BigInt(params.feeBufferLamports ?? 50_000); // ~0.00005 SOL default buffer
  if (BigInt(bal) < lamports + feeBuf) {
    throw new Error(
      `Insufficient SOL (including fee buffer).\n` +
        `- balance: ${bal} lamports\n` +
        `- contribute: ${params.lamports} lamports\n` +
        `- feeBuffer: ${feeBuf.toString()} lamports\n`
    );
  }

  // Build instruction using the shared IDL-based builder
  const ix = buildContributeV2Instruction({
    programId,
    wallet: feePayer,
    pool_v2: poolPk,
    member_v2: memberPk,
    vault_v2: vaultPk,
    lamports: asTrimmed(params.lamports),
  });

  // Blockhash for v0 tx
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash(commitment);

  const msg = new TransactionMessage({
    payerKey: feePayer,
    recentBlockhash: blockhash,
    instructions: [ix],
  }).compileToV0Message();

  const vtx = new VersionedTransaction(msg);

  // Sign + Send (standardize: prefer signTransaction path; fallback to signAndSendTransaction if needed)
  let sig: string;

  if (canSignTx) {
    const signed = await wallet.signTransaction(vtx);
    const raw = signed.serialize();
    sig = await connection.sendRawTransaction(raw, {
      skipPreflight: false,
      preflightCommitment: commitment,
    });
  } else {
    // Fallback: some injected contexts only offer signAndSendTransaction
    const res = await wallet.signAndSendTransaction(vtx);
    sig = res?.signature ? String(res.signature) : String(res);
  }

  // Confirm
  const conf = await connection.confirmTransaction(
    { signature: sig, blockhash, lastValidBlockHeight },
    commitment
  );

  if (conf.value.err) {
    throw new Error(`Transaction confirmed with error: ${JSON.stringify(conf.value.err)}`);
  }

  return {
    signature: sig,
    commitment,
    blockhash,
    lastValidBlockHeight,
    confirmation: conf,

    // return effective accounts (useful for receipts/telemetry)
    effective: {
      feePayer: feePayer.toBase58(),
      pool_v2: poolPk.toBase58(),
      member_v2: memberPk.toBase58(),
      vault_v2: vaultPk.toBase58(),
      derived: {
        member_v2: derivedMember.toBase58(),
        vault_v2: derivedVault.toBase58(),
      },
    },
  };
}
